import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Package,
  Settings,
  ToggleLeft,
  ToggleRight,
  Eye,
  DollarSign,
  Shield,
  Activity,
  Zap,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

import { API } from '../../lib/api';

const moduleIcons = {
  observability: Eye,
  'cost-intelligence': DollarSign,
  reliability: Activity,
  security: Shield,
  automation: Zap,
};

const categoryColors = {
  observability: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/30' },
  cost: { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/30' },
  reliability: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/30' },
  security: { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/30' },
  automation: { bg: 'bg-purple-500/10', text: 'text-purple-500', border: 'border-purple-500/30' },
};

export const Modules = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configValues, setConfigValues] = useState({});

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/admin/modules`);
      setModules(response.data?.modules || []);
    } catch (error) {
      console.error('Error fetching modules:', error);
      if (error?.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error(error?.response?.data?.detail || 'Failed to load platform modules');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = async (moduleId) => {
    try {
      await axios.put(`${API}/admin/modules/${moduleId}/toggle`);
      setModules(prev => prev.map(m => 
        m.id === moduleId ? { ...m, enabled: !m.enabled } : m
      ));
      toast.success('Module toggled');
    } catch (error) {
      toast.error('Failed to toggle module');
    }
  };

  const openConfig = (module) => {
    setSelectedModule(module);
    setConfigValues(module.config || {});
    setConfigDialogOpen(true);
  };

  const saveConfig = async () => {
    try {
      await axios.put(`${API}/admin/modules/${selectedModule.id}/config`, configValues);
      setModules(prev => prev.map(m =>
        m.id === selectedModule.id ? { ...m, config: configValues } : m
      ));
      toast.success('Configuration saved');
      setConfigDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save configuration');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="max-w-2xl mx-auto pt-16 px-6" data-testid="modules-denied">
        <Card className="p-8 text-center border-amber-500/30 bg-amber-500/5">
          <Package className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold">Super-admin only</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Platform module configuration is restricted to super-admins.
          </p>
        </Card>
      </div>
    );
  }

  const enabledCount = modules.filter(m => m.enabled).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="modules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Platform Modules</h2>
          <p className="text-muted-foreground">Configure and manage platform capabilities</p>
        </div>
        <Badge variant="outline" className="text-primary border-primary/30">
          {enabledCount}/{modules.length} Enabled
        </Badge>
      </div>

      {/* Modules Grid */}
      {modules.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center" data-testid="modules-empty">
          <Package className="h-10 w-10 text-muted-foreground/50 mb-3" />
          <p className="text-sm text-muted-foreground">No platform modules are registered yet.</p>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {modules.map((module) => {
          const Icon = moduleIcons[module.id] || Package;
          const colors = categoryColors[module.category] || categoryColors.observability;

          return (
            <Card
              key={module.id}
              className={`bg-card border-l-4 ${module.enabled ? colors.border : 'border-muted'} transition-all hover:shadow-lg`}
              data-testid={`module-${module.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg ${colors.bg}`}>
                      <Icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{module.name}</CardTitle>
                      <Badge variant="secondary" className="mt-1 text-xs">
                        v{module.version}
                      </Badge>
                    </div>
                  </div>
                  <Switch
                    checked={module.enabled}
                    onCheckedChange={() => toggleModule(module.id)}
                    data-testid={`toggle-${module.id}`}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{module.description}</p>
                
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="flex items-center gap-2">
                    {module.enabled ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <div className="w-4 h-4 rounded-full bg-muted" />
                    )}
                    <span className={`text-sm ${module.enabled ? 'text-green-500' : 'text-muted-foreground'}`}>
                      {module.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openConfig(module)}
                    data-testid={`config-${module.id}`}
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Configure
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      {/* Configuration Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Configure {selectedModule?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedModule && Object.entries(selectedModule.config || {}).map(([key, value]) => (
              <div key={key} className="space-y-2">
                <Label htmlFor={key} className="capitalize">
                  {key.replace(/_/g, ' ')}
                </Label>
                {typeof value === 'boolean' ? (
                  <Switch
                    id={key}
                    checked={configValues[key] || false}
                    onCheckedChange={(checked) => setConfigValues(prev => ({ ...prev, [key]: checked }))}
                  />
                ) : typeof value === 'number' ? (
                  <Input
                    id={key}
                    type="number"
                    value={configValues[key] || ''}
                    onChange={(e) => setConfigValues(prev => ({ ...prev, [key]: parseFloat(e.target.value) }))}
                    className="bg-secondary border-border"
                  />
                ) : Array.isArray(value) ? (
                  <Input
                    id={key}
                    value={(configValues[key] || []).join(', ')}
                    onChange={(e) => setConfigValues(prev => ({ ...prev, [key]: e.target.value.split(', ') }))}
                    className="bg-secondary border-border"
                    placeholder="Comma separated values"
                  />
                ) : typeof value === 'object' ? (
                  <pre className="p-2 bg-secondary rounded text-xs">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                ) : (
                  <Input
                    id={key}
                    value={configValues[key] || ''}
                    onChange={(e) => setConfigValues(prev => ({ ...prev, [key]: e.target.value }))}
                    className="bg-secondary border-border"
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveConfig} data-testid="save-config">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Save Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Modules;
